// Iter94b — Admin Promo Codes Manager (Phase 2).
// Iter98 — Extended:
//   • Per-code allowlist picker (multi-select of packages, search + tick).
//   • PUT edit flow (every field except the immutable code string).
//   • Analytics panel with date-range filter + revenue-lift bar chart.
// -----------------------------------------------------------------------------
import { useEffect, useMemo, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Ticket, Plus, Trash2, ChevronDown, ChevronUp, Pencil, X,
  Search, BarChart3, Check, Play, Pause,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const DISCOUNT_TYPES = [
  { id: "percent", label: "% off" },
  { id: "fixed", label: "£ off" },
];

const RANGE_TABS = [
  { id: 0, label: "All-time" },
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
];

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const codesCtl = useListControls(codes, { name: (c) => c.code, fields: (c) => [c.code, c.description, c.label], initialSort: "default" });
  const [packages, setPackages] = useState([]);   // pool for allowlist picker
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // promo doc currently being edited

  const load = async () => {
    setLoading(true);
    try {
      // Fetch codes + package pool in parallel — the pool feeds the allowlist
      // picker on both the create form and the edit modal so we only pay the
      // network cost once.
      const [codesRes, pkgsRes] = await Promise.all([
        api.get("/admin/promo-codes"),
        api.get("/offerings?packages_only=true"),
      ]);
      setCodes(Array.isArray(codesRes.data) ? codesRes.data : []);
      setPackages(Array.isArray(pkgsRes.data) ? pkgsRes.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load promo codes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activeCount = codes.filter((c) => c.active).length;
  const totalRedemptions = codes.reduce((n, c) => n + (c.uses_count || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white px-4 sm:px-8 py-6 space-y-5" data-testid="admin-promo-codes-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white" data-testid="promo-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Admin console
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-[#CCFF00] mb-1">PROMO CODES · PHASE 2</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Discount codes.</h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-3xl">
            One code per checkout — codes never stack. A code applies to a package only when both
            sides agree: the coach has ticked <span className="text-white">Allow discount codes</span> on the
            package <em>and</em> either the code&apos;s allowlist is empty (universal) or lists that package.
            Deactivating a code preserves historic redemptions.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="btn-volt bg-[#CCFF00] text-black px-4 py-2.5 text-xs font-semibold hover:brightness-95 inline-flex items-center gap-2"
          data-testid="new-promo-toggle"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Close" : "New code"}
        </button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-3" data-testid="promo-stats">
        <StatBox label="Total codes" value={codes.length} testid="promo-stat-total" />
        <StatBox label="Active" value={activeCount} testid="promo-stat-active" />
        <StatBox label="Redemptions" value={totalRedemptions} testid="promo-stat-uses" />
      </div>

      {showForm && <PromoForm packages={packages} onCreated={() => { setShowForm(false); load(); }} />}

      {/* Iter98 — Analytics panel. Always visible under the stat strip so
          marketing can see revenue lift without scrolling past the list. */}
      <PromoAnalytics />

      {loading && <div className="text-sm text-[#A3A3A3]" data-testid="promo-loading">Loading…</div>}

      {!loading && codes.length === 0 && (
        <div className="card-surface p-8 text-center" data-testid="promo-empty">
          <Ticket className="w-10 h-10 text-[#262626] mx-auto mb-3" strokeWidth={1.5} />
          <div className="h-display text-xl mb-1">No promo codes yet</div>
          <div className="text-xs text-[#A3A3A3]">Create your first one above.</div>
        </div>
      )}

      <div className="space-y-2" data-testid="promo-list">
        {codes.length > 0 && <ListControlsBar ctl={codesCtl} modes={["default", "az", "za"]} placeholder="Search promo codes…" testid="promo" />}
        {codesCtl.list.map((c) => (
          <PromoRow
            key={c.id}
            code={c}
            packages={packages}
            onEdit={() => setEditing(c)}
            onChanged={load}
          />
        ))}
      </div>

      {editing && (
        <PromoEditModal
          code={editing}
          packages={packages}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="card-surface p-3" data-testid={testid}>
      <div className="label-mono text-[#A3A3A3]">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Allowlist picker — shared between create form and edit modal. Multi-select
// with an inline search. Empty selection = "applies to all packages".
// ---------------------------------------------------------------------------
function AllowlistPicker({ packages, selectedIds, onChange, testidPrefix = "allow" }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "", "en-GB");
    if (!term) return [...packages].sort(byTitle);
    return packages.filter((p) =>
      (p.title || "").toLowerCase().includes(term) ||
      (p.coach_name || "").toLowerCase().includes(term),
    ).sort(byTitle);
  }, [packages, q]);
  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };
  return (
    <div className="border border-[#262626] p-3 space-y-2" data-testid={`${testidPrefix}-picker`}>
      <div className="flex items-center justify-between">
        <div className="label-mono text-[#A3A3A3]">
          {selectedIds.length === 0
            ? "APPLIES TO EVERY PACKAGE"
            : `APPLIES TO ${selectedIds.length} SELECTED PACKAGE${selectedIds.length === 1 ? "" : "S"}`}
        </div>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-[#A3A3A3] hover:text-white"
            data-testid={`${testidPrefix}-clear`}
          >
            Clear (applies to all)
          </button>
        )}
      </div>
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-[#525252] absolute left-2 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search packages by title or coach"
          className="w-full bg-[#0A0A0A] border border-[#262626] pl-7 pr-3 py-1.5 text-xs focus:border-[#CCFF00] focus:outline-none"
          data-testid={`${testidPrefix}-search`}
        />
      </div>
      <div className="max-h-44 overflow-y-auto space-y-1 pr-1" data-testid={`${testidPrefix}-options`}>
        {filtered.length === 0 && (
          <div className="text-[11px] text-[#525252] italic py-1">No packages match.</div>
        )}
        {filtered.map((p) => {
          const on = selectedIds.includes(p.id);
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => toggle(p.id)}
              className="w-full flex items-center gap-2 text-left px-2 py-1.5 hover:bg-[#141414] transition-colors border border-transparent"
              style={on ? { borderColor: "#CCFF00" } : {}}
              data-testid={`${testidPrefix}-opt-${p.id}`}
            >
              <span className="w-4 h-4 border flex items-center justify-center shrink-0"
                    style={on ? { background: "#CCFF00", borderColor: "#CCFF00" } : { borderColor: "#525252" }}>
                {on && <Check className="w-3 h-3 text-black" />}
              </span>
              <span className="text-xs text-white truncate">{p.title}</span>
              <span className="text-[10px] text-[#A3A3A3] shrink-0">£{p.price_per_player}</span>
              <span className="text-[10px] text-[#525252] shrink-0">· {p.coach_name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------
function PromoForm({ packages, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    discount_value: 20,
    expires_at: "",
    notes: "",
    max_uses_total: "",
    allowed_offering_ids: [],
    // Iter307 — targets festival passes vs coaching packages.
    applies_to: "package",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        expires_at: form.expires_at ? `${form.expires_at}T23:59:59+00:00` : null,
        notes: form.notes || null,
        max_uses_total: form.max_uses_total ? Number(form.max_uses_total) : null,
        allowed_offering_ids: form.allowed_offering_ids,
        applies_to: form.applies_to,
      });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't create");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="card-surface p-5 space-y-4 fade-up" data-testid="promo-form">
      <div className="grid md:grid-cols-3 gap-4">
        <label className="block">
          <div className="label-mono mb-1.5">Code</div>
          <input
            value={form.code}
            onChange={(e) => set("code", e.target.value.toUpperCase())}
            required
            minLength={3}
            maxLength={32}
            placeholder="WELCOME20"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#CCFF00] focus:outline-none font-mono uppercase"
            data-testid="promo-form-code"
          />
        </label>
        <label className="block">
          <div className="label-mono mb-1.5">Discount type</div>
          <div className="grid grid-cols-2 gap-1">
            {DISCOUNT_TYPES.map((t) => (
              <button
                type="button"
                key={t.id}
                onClick={() => set("discount_type", t.id)}
                data-testid={`promo-form-type-${t.id}`}
                className="px-2 py-2 text-xs uppercase tracking-wider border transition-colors"
                style={form.discount_type === t.id
                  ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </label>
        <label className="block">
          <div className="label-mono mb-1.5">Value {form.discount_type === "percent" ? "(%)" : "(£)"}</div>
          <input
            type="number"
            value={form.discount_value}
            onChange={(e) => set("discount_value", e.target.value)}
            required
            min={1}
            max={form.discount_type === "percent" ? 100 : 10000}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#CCFF00] focus:outline-none"
            data-testid="promo-form-value"
          />
        </label>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <label className="block">
          <div className="label-mono mb-1.5">Expires (optional)</div>
          <input
            type="date"
            value={form.expires_at}
            onChange={(e) => set("expires_at", e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#CCFF00] focus:outline-none"
            data-testid="promo-form-expires"
          />
        </label>
        <label className="block">
          <div className="label-mono mb-1.5">Max total uses (optional)</div>
          <input
            type="number"
            value={form.max_uses_total}
            onChange={(e) => set("max_uses_total", e.target.value)}
            min={1}
            placeholder="Unlimited"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#CCFF00] focus:outline-none"
            data-testid="promo-form-max-uses"
          />
        </label>
        <label className="block">
          <div className="label-mono mb-1.5">Internal notes</div>
          <input
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            maxLength={400}
            placeholder="Sept marketing campaign"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm focus:border-[#CCFF00] focus:outline-none"
            data-testid="promo-form-notes"
          />
        </label>
      </div>
      <div>
        <div className="label-mono mb-1.5">Restrict to specific packages</div>
        <AllowlistPicker
          packages={packages}
          selectedIds={form.allowed_offering_ids}
          onChange={(ids) => set("allowed_offering_ids", ids)}
          testidPrefix="promo-form-allow"
        />
      </div>
      {/* Iter307 — Applies-to toggle. Owners can now scope a code to
          festival passes (or both surfaces) from the UI instead of
          editing Mongo directly. */}
      <div>
        <div className="label-mono mb-1.5">Applies to</div>
        <div className="grid grid-cols-3 gap-1 max-w-md">
          {[
            { id: "package", label: "Coaching only" },
            { id: "festival_pass", label: "Festival passes" },
            { id: "any", label: "Both" },
          ].map((t) => (
            <button
              type="button"
              key={t.id}
              onClick={() => set("applies_to", t.id)}
              data-testid={`promo-form-applies-${t.id}`}
              className="px-2 py-2 text-xs uppercase tracking-wider border transition-colors"
              style={form.applies_to === t.id
                ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                : { borderColor: "#262626", color: "#A3A3A3" }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="btn-volt bg-[#CCFF00] text-black px-4 py-2 text-xs font-semibold disabled:opacity-50"
          data-testid="promo-form-submit"
        >
          {saving ? "Creating…" : "Create code"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Edit modal — everything except the code string is editable.
// ---------------------------------------------------------------------------
function PromoEditModal({ code, packages, onClose, onSaved }) {
  const initialExp = code.expires_at ? (code.expires_at || "").slice(0, 10) : "";
  const [form, setForm] = useState({
    discount_type: code.discount_type,
    discount_value: code.discount_value,
    expires_at: initialExp,
    notes: code.notes || "",
    max_uses_total: code.max_uses_total || "",
    allowed_offering_ids: Array.isArray(code.allowed_offering_ids) ? [...code.allowed_offering_ids] : [],
    active: code.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/promo-codes/${code.id}`, {
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        expires_at: form.expires_at ? `${form.expires_at}T23:59:59+00:00` : "",
        notes: form.notes,
        max_uses_total: form.max_uses_total ? Number(form.max_uses_total) : 0,
        allowed_offering_ids: form.allowed_offering_ids,
        active: form.active,
      });
      toast.success(`${code.code} updated`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="promo-edit-modal">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#262626] p-6 space-y-4 my-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono text-[#CCFF00]">EDIT CODE</div>
            <div className="h-display text-2xl font-mono">{code.code}</div>
            <div className="text-[11px] text-[#525252] mt-1">Code string itself is locked to preserve audit trail.</div>
          </div>
          <button onClick={onClose} className="p-1 text-[#A3A3A3] hover:text-white" data-testid="promo-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <div className="label-mono mb-1.5">Discount type</div>
            <div className="grid grid-cols-2 gap-1">
              {DISCOUNT_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => set("discount_type", t.id)}
                  data-testid={`promo-edit-type-${t.id}`}
                  className="px-2 py-2 text-xs uppercase tracking-wider border transition-colors"
                  style={form.discount_type === t.id
                    ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                    : { borderColor: "#262626", color: "#A3A3A3" }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <div className="label-mono mb-1.5">Value {form.discount_type === "percent" ? "(%)" : "(£)"}</div>
            <input
              type="number"
              value={form.discount_value}
              onChange={(e) => set("discount_value", e.target.value)}
              min={1}
              max={form.discount_type === "percent" ? 100 : 10000}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] focus:outline-none"
              data-testid="promo-edit-value"
            />
          </label>
          <label className="block">
            <div className="label-mono mb-1.5">Expires</div>
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => set("expires_at", e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] focus:outline-none"
              data-testid="promo-edit-expires"
            />
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <div className="label-mono mb-1.5">Max total uses</div>
            <input
              type="number"
              value={form.max_uses_total}
              onChange={(e) => set("max_uses_total", e.target.value)}
              min={0}
              placeholder="Unlimited"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] focus:outline-none"
              data-testid="promo-edit-max-uses"
            />
          </label>
          <label className="block">
            <div className="label-mono mb-1.5">Internal notes</div>
            <input
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              maxLength={400}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] focus:outline-none"
              data-testid="promo-edit-notes"
            />
          </label>
        </div>
        <div>
          <div className="label-mono mb-1.5">Restrict to specific packages</div>
          <AllowlistPicker
            packages={packages}
            selectedIds={form.allowed_offering_ids}
            onChange={(ids) => set("allowed_offering_ids", ids)}
            testidPrefix="promo-edit-allow"
          />
        </div>
        <div className="flex items-center justify-between border-t border-[#262626] pt-4">
          <button
            type="button"
            onClick={() => set("active", !form.active)}
            className="text-xs inline-flex items-center gap-2 border px-3 py-2"
            style={form.active
              ? { borderColor: "#CCFF00", color: "#CCFF00" }
              : { borderColor: "#525252", color: "#A3A3A3" }}
            data-testid="promo-edit-active-toggle"
          >
            {form.active ? <><Play className="w-3.5 h-3.5" /> ACTIVE</> : <><Pause className="w-3.5 h-3.5" /> PAUSED</>}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-2 border border-[#262626] text-[#A3A3A3] hover:text-white"
              data-testid="promo-edit-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-xs px-4 py-2 bg-[#CCFF00] text-black font-semibold disabled:opacity-50"
              data-testid="promo-edit-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row — same as before + Edit button + allowlist chip.
// ---------------------------------------------------------------------------
function PromoRow({ code, packages, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const disable = async () => {
    if (!window.confirm(`Deactivate promo code ${code.code}?\n\nHistoric redemptions stay visible.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/promo-codes/${code.id}`);
      toast.success(`${code.code} deactivated`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  const humanDisc = code.discount_type === "percent"
    ? `${code.discount_value}% off`
    : `£${code.discount_value} off`;
  const humanExp = code.expires_at ? new Date(code.expires_at).toLocaleDateString() : "no expiry";
  const allowlist = code.allowed_offering_ids || [];
  const allowlistTitles = allowlist.length > 0
    ? allowlist
        .map((id) => packages.find((p) => p.id === id)?.title || "Deleted package")
        .slice(0, 3)
        .join(", ") + (allowlist.length > 3 ? ` +${allowlist.length - 3}` : "")
    : "";
  return (
    <div className="border border-[#262626] p-3" data-testid={`promo-row-${code.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="label-mono text-lg text-white font-mono">{code.code}</span>
        <span className="text-xs text-[#A3A3A3]">{humanDisc}</span>
        <span className="text-xs text-[#525252]">·</span>
        <span className="text-xs text-[#A3A3A3]">{humanExp}</span>
        {allowlist.length > 0 && (
          <span
            className="label-mono text-[10px] px-1.5 py-0.5 border border-[#CCFF00] text-[#CCFF00]"
            title={allowlistTitles}
            data-testid={`promo-allowlist-${code.id}`}
          >
            {allowlist.length} PACKAGE{allowlist.length === 1 ? "" : "S"}
          </span>
        )}
        {!code.active && (
          <span className="label-mono text-[10px] px-1.5 py-0.5 bg-[#525252] text-white" data-testid={`promo-inactive-${code.id}`}>PAUSED</span>
        )}
        <span className="ml-auto text-xs text-[#A3A3A3]" data-testid={`promo-uses-${code.id}`}>
          {code.uses_count} use{code.uses_count === 1 ? "" : "s"}{code.max_uses_total ? ` / ${code.max_uses_total}` : ""}
        </span>
        {(code.redemptions || []).length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            data-testid={`promo-expand-${code.id}`}
            className="text-xs text-[#CCFF00] hover:opacity-70 inline-flex items-center gap-1"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Hide" : "Show"} redemptions
          </button>
        )}
        <button
          onClick={onEdit}
          className="text-xs text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
          data-testid={`promo-edit-${code.id}`}
        >
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        {code.active && (
          <button
            onClick={disable}
            disabled={busy}
            className="text-xs text-[#FF4500] hover:opacity-70 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`promo-deactivate-${code.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Deactivate
          </button>
        )}
      </div>
      {code.notes && (
        <div className="text-[11px] text-[#A3A3A3] mt-1.5" data-testid={`promo-notes-${code.id}`}>{code.notes}</div>
      )}
      {expanded && (code.redemptions || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#262626] space-y-2" data-testid={`promo-redemptions-${code.id}`}>
          {(code.redemptions || []).map((r, idx) => (
            <div key={idx} className="text-xs text-[#A3A3A3] flex items-center gap-2">
              <span className="text-white">{r.student_email || r.student_id}</span>
              <span>·</span>
              <span>saved £{r.discount_applied}</span>
              <span>·</span>
              <span>{new Date(r.redeemed_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analytics panel — date-range tabs + revenue-lift bar chart + summary tiles.
// ---------------------------------------------------------------------------
function PromoAnalytics() {
  const [days, setDays] = useState(0);
  const [data, setData] = useState({ rows: [], totals: { redemptions: 0, revenue_before: 0, discount_given: 0, revenue_after: 0 } });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/admin/promo-codes/analytics${days ? `?days=${days}` : ""}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || "Analytics failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  // Show only redeemed codes on the chart — a wall of zero-use codes is noise.
  const chartRows = (data.rows || []).filter((r) => r.redemptions > 0).slice(0, 8);
  const totals = data.totals || {};

  return (
    <div className="card-surface p-5 space-y-4" data-testid="promo-analytics">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#CCFF00]" />
          <div className="label-mono text-[#CCFF00]">REVENUE LIFT</div>
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="text-xs text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 ml-2"
            data-testid="promo-analytics-toggle"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Hide" : "Show"}
          </button>
        </div>
        <div className="flex items-center gap-1" data-testid="promo-analytics-range">
          {RANGE_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setDays(t.id)}
              className="px-2.5 py-1 text-[11px] uppercase tracking-wider border transition-colors"
              style={days === t.id
                ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                : { borderColor: "#262626", color: "#A3A3A3" }}
              data-testid={`promo-range-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {expanded && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatTile label="Redemptions" value={totals.redemptions || 0} testid="analytics-total-redemptions" />
            <StatTile label="Revenue (gross)" value={`£${(totals.revenue_before || 0).toFixed(2)}`} testid="analytics-revenue-before" />
            <StatTile label="Discount given" value={`£${(totals.discount_given || 0).toFixed(2)}`} accent="#FF4500" testid="analytics-discount" />
            <StatTile label="Revenue (net)" value={`£${(totals.revenue_after || 0).toFixed(2)}`} accent="#CCFF00" testid="analytics-revenue-after" />
          </div>

          {loading && <div className="text-xs text-[#A3A3A3]" data-testid="promo-analytics-loading">Loading…</div>}

          {!loading && chartRows.length === 0 && (
            <div className="border border-[#262626] p-6 text-center text-xs text-[#A3A3A3]" data-testid="promo-analytics-empty">
              No redemptions in this window yet. Share a code with a student to see revenue lift here.
            </div>
          )}

          {!loading && chartRows.length > 0 && (
            <div className="border border-[#262626] p-3" data-testid="promo-analytics-chart">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartRows} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="code" stroke="#A3A3A3" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                  <YAxis stroke="#A3A3A3" tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", fontSize: 12 }}
                    formatter={(v, name) => [`£${Number(v).toFixed(2)}`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue_after" name="Revenue (net)" fill="#CCFF00" />
                  <Bar dataKey="discount_given" name="Discount given" fill="#FF4500" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div className="border border-[#262626] p-3" data-testid={testid}>
      <div className="label-mono text-[#A3A3A3]">{label}</div>
      <div className="text-xl font-bold mt-0.5" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
